// useGeeTestVerification.js
import { useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { initGeeTest } from '../resource/geetest.js';
import { SET_OPERATION_DATA, REMOVE_OPERATION_DATA } from '../ActionTypes.js';
import {
  GEETEST_CAPTCHA_ID,
  CAPTCHA_VERIFICATION_TIMEOUT_SEC,
} from '../RemoteConfigKeys.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import { getCaptchaProvider } from '../selector/getCaptchaHeaders.js';
import pushToastr from '../action/pushToastr.js';
import env from '../resource/env.js';
import { captcha } from '../resource/debug.js';
import { EMPTY_OBJECT, NULL_FUNCTION } from '../resource/defaults.js';
import { getSentry } from '../resource/sentry.js';

const log = captcha.extend('log:useGeeTestVerification');
const errorLog = captcha.extend('error:useGeeTestVerification');

/**
 * Hook for GeeTest CAPTCHA verification
 *
 * Flow:
 * 1. Component calls triggerVerification()
 * 2. GeeTest popup appears for user to complete verification
 * 3. On success → onVerificationSuccess callback is called
 *
 * @kind hook
 * @param {Object} options - Configuration options
 * @param {Function} options.onVerificationSuccess - Callback when user completes verification successfully
 * @param {Function} options.onCaptchaLoadEnd - Callback when CAPTCHA loading phase ends (success or failed)
 * @return {Object} { triggerGeeTestVerification }
 */
const useGeeTestVerification = (options = EMPTY_OBJECT) => {
  const {
    onVerificationSuccess = NULL_FUNCTION,
    onCaptchaLoadEnd = NULL_FUNCTION,
  } = options;

  const dispatch = useDispatch();
  const captchaProvider = useSelector(state => getCaptchaProvider(state));
  const geetestCaptchaId = useSelector(state =>
    getRemoteConfigData(state, GEETEST_CAPTCHA_ID)
  );
  const verificationTimeoutSeconds =
    useSelector(state =>
      getRemoteConfigData(state, CAPTCHA_VERIFICATION_TIMEOUT_SEC)
    ) || env.CAPTCHA_VERIFICATION_TIMEOUT_SEC;

  log('[useGeeTestVerification] init', {
    captchaProvider,
    geetestCaptchaId,
    verificationTimeoutSeconds,
  });

  const instanceRef = useRef(null);
  const initPromiseRef = useRef(null);
  const verificationTimeoutRef = useRef(null);
  const onVerificationSuccessRef = useRef(onVerificationSuccess);
  const destroyedRef = useRef(false);

  const clearVerificationTimeout = useCallback(() => {
    if (verificationTimeoutRef.current) {
      clearTimeout(verificationTimeoutRef.current);
      verificationTimeoutRef.current = null;
      log('[useGeeTestVerification] verification timeout cleared');
    }
  }, []);

  // Update ref to latest callback on every render
  useEffect(() => {
    onVerificationSuccessRef.current = onVerificationSuccess;
  }, [onVerificationSuccess]);

  const initializeGeeTest = useCallback(async () => {
    destroyedRef.current = false;

    // Set initPromiseRef so triggerVerification can wait for it
    initPromiseRef.current = initGeeTest({
      captchaId: geetestCaptchaId,
      product: 'bind', //bind mode means no visible widget, use showCaptcha() to trigger the verification popup
      onSuccess: result => {
        log('[GeeTest] verification success', result);
        if (destroyedRef.current) return;

        clearVerificationTimeout();

        // Save verification result to Redux
        dispatch({
          type: SET_OPERATION_DATA,
          payload: {
            selectPath: ['geetest'],
            data: {
              lotNumber: result.lot_number,
              captchaOutput: result.captcha_output,
              passToken: result.pass_token,
              genTime: result.gen_time,
              captchaId: result.captcha_id,
            },
          },
        });

        if (onVerificationSuccessRef.current)
          onVerificationSuccessRef.current(result);
      },
      onError: error => {
        errorLog('[GeeTest] error', error);
        if (destroyedRef.current) return;
        clearVerificationTimeout();
      },
      onReady: () => {
        log('[GeeTest] ready');
        if (destroyedRef.current) return;
      },
      onFail: () => {
        log('[GeeTest] verification failed - user can retry');
        if (destroyedRef.current) return;
        clearVerificationTimeout();
      },
    });

    try {
      const instance = await initPromiseRef.current;

      if (destroyedRef.current) {
        instance?.destroy?.();
        return;
      }

      instanceRef.current = instance;
      log('[GeeTest] initialized successfully');
    } catch (error) {
      errorLog('[GeeTest] init failed', error);
    } finally {
      initPromiseRef.current = null;
    }
  }, [geetestCaptchaId, clearVerificationTimeout, dispatch]);

  const destroyGeeTest = useCallback(() => {
    destroyedRef.current = true;

    // Clear verification timeout
    clearVerificationTimeout();

    // Destroy instance
    if (instanceRef.current?.destroy) {
      try {
        instanceRef.current.destroy();
        log('[GeeTest] instance destroyed');
      } catch (error) {
        errorLog('[GeeTest] cleanup error', error);
      }
    }
    instanceRef.current = null;

    // Clear Redux
    dispatch({
      type: REMOVE_OPERATION_DATA,
      payload: {
        selectPath: ['geetest'],
      },
    });
  }, [clearVerificationTimeout, dispatch]);

  /**
   * Destroy current GeeTest instance and re-initialize a new one.
   * Use when a fresh instance is needed (e.g. resend OTP) to avoid
   * backend CAPTCHA_TOO_LONG errors.
   */
  const resetGeeTestInstance = useCallback(() => {
    log('[GeeTest] resetGeeTestInstance');
    destroyGeeTest();
    initializeGeeTest();
  }, [destroyGeeTest, initializeGeeTest]);

  // Initialize GeeTest instance
  useEffect(() => {
    if (captchaProvider !== 'geetest' || !geetestCaptchaId) {
      return;
    }

    initializeGeeTest();

    return () => {
      destroyGeeTest();
    };
  }, [captchaProvider, geetestCaptchaId, initializeGeeTest, destroyGeeTest]);

  /**
   * Trigger GeeTest verification popup
   */
  const triggerVerification = useCallback(async () => {
    if (captchaProvider !== 'geetest') {
      log('[triggerVerification] not GeeTest provider, skipping');
      return;
    }

    // Wait for loading script promise if still in progress
    if (!instanceRef.current && initPromiseRef.current) {
      log('[triggerVerification] waiting for init in progress');
      const waitStartTime = Date.now();
      getSentry().then(({ withScope, captureMessage }) => {
        withScope(scope => {
          scope.setFingerprint(['captcha', 'geetest', 'waiting_for_init']);
          scope.setTag('record', 'captcha');
          scope.setTag('captcha_provider', 'geetest');
          scope.setTag('captcha_stage', 'waiting_for_init');
          scope.setExtra('captchaId', geetestCaptchaId);
          captureMessage('GeeTest waiting for init when triggered');
        });
      });
      try {
        instanceRef.current = await initPromiseRef.current;
      } catch (error) {
        errorLog('[triggerVerification] init failed while waiting', error);
        getSentry().then(({ withScope, captureException }) => {
          withScope(scope => {
            scope.setFingerprint(['captcha', 'geetest', 'init_wait_failed']);
            scope.setTag('record', 'captcha');
            scope.setTag('captcha_provider', 'geetest');
            scope.setTag('captcha_stage', 'init_wait_failed');
            scope.setExtra('captchaId', geetestCaptchaId);
            scope.setExtra('error_message', error?.message);
            scope.setExtra('wait_duration_ms', Date.now() - waitStartTime);
            captureException(error);
          });
        });
        onCaptchaLoadEnd(); // Clear loading timeout from useCaptchaForm
        dispatch(
          pushToastr({
            textKey: 'system_processing_wait_or_try_later',
            attachedMessage: '(SDK_FAILED)',
            color: 'error',
            durationSec: 10,
          })
        );
        return;
      }
    }

    // no instance, initialization failed
    if (!instanceRef.current) {
      errorLog('[triggerVerification] instance not available');
      getSentry().then(({ withScope, captureMessage }) => {
        withScope(scope => {
          scope.setFingerprint(['captcha', 'geetest', 'instance_unavailable']);
          scope.setTag('record', 'captcha');
          scope.setTag('captcha_provider', 'geetest');
          scope.setTag('captcha_stage', 'instance_unavailable');
          scope.setExtra('captchaId', geetestCaptchaId);
          scope.setExtra('has_init_promise', !!initPromiseRef.current);
          captureMessage('GeeTest instance unavailable when triggered');
        });
      });
      onCaptchaLoadEnd(); // Clear loading timeout from useCaptchaForm
      dispatch(
        pushToastr({
          textKey: 'system_processing_wait_or_try_later',
          attachedMessage: '(SDK_FAILED)',
          color: 'error',
          durationSec: 10,
        })
      );
      return;
    }

    try {
      log('[triggerVerification] calling showCaptcha()');
      instanceRef.current.showCaptcha();

      onCaptchaLoadEnd();

      // Start verification timeout to show timeout error toast
      verificationTimeoutRef.current = setTimeout(() => {
        log('[triggerVerification] verification timeout - showing toast');
        getSentry().then(({ withScope, captureMessage }) => {
          withScope(scope => {
            scope.setFingerprint([
              'captcha',
              'geetest',
              'verification_timeout',
            ]);
            scope.setTag('record', 'captcha');
            scope.setTag('captcha_provider', 'geetest');
            scope.setTag('captcha_stage', 'verification_timeout');
            scope.setExtra('timeout_seconds', verificationTimeoutSeconds);
            captureMessage('GeeTest verification timeout');
          });
        });
        dispatch(
          pushToastr({
            textKey: 'kyc_failure_general',
            color: 'error',
            durationSec: 10,
          })
        );
      }, verificationTimeoutSeconds * 1000);
      log('[triggerVerification] verification timeout started', {
        verificationTimeoutSeconds,
      });
    } catch (error) {
      errorLog('[triggerVerification] showCaptcha() failed', error);
      getSentry().then(({ withScope, captureException }) => {
        withScope(scope => {
          scope.setFingerprint(['captcha', 'geetest', 'show_captcha_failed']);
          scope.setTag('record', 'captcha');
          scope.setTag('captcha_provider', 'geetest');
          scope.setTag('captcha_stage', 'show_captcha_failed');
          scope.setExtra('error_message', error?.message);
          captureException(error);
        });
      });
    }
  }, [
    captchaProvider,
    dispatch,
    verificationTimeoutSeconds,
    geetestCaptchaId,
    onCaptchaLoadEnd,
  ]);

  return {
    triggerGeeTestVerification: triggerVerification,
    resetGeeTestInstance,
  };
};

export default useGeeTestVerification;
